import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Divider,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useData } from "../../Contexts/DataContext";

function BulkAddRequirementsModal({ isOpen, onClose }) {
  const toast = useToast();
  const { selectedSow, addDocument, selectedClient } = useData();
  const deliverables = selectedSow?.deliverables || [];
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** Default requirement structure */
  const defaultRequirement = {
    title: "",
    deliverableId: "",
    detail: "",
    assignedTo: "",
    dueDate: "",
    raisedBy: "",
    completed: false,
    status: "Not Started",
  };

  /** State for multiple requirements (1 row initially) */
  const [requirements, setRequirements] = useState([{ ...defaultRequirement }]);

  /** Handle Input Change */
  const handleInputChange = (index, e) => {
    const { name, value } = e.target;
    const updatedRequirements = [...requirements];
    updatedRequirements[index][name] = value;
    setRequirements(updatedRequirements);
  };

  /** Add New Row */
  const addRequirement = () => {
    setRequirements([...requirements, { ...defaultRequirement }]);
  };

  /** Remove Row */
  const removeRequirement = (index) => {
    if (requirements.length > 1) {
      setRequirements(requirements.filter((_, i) => i !== index));
    }
  };

  /** Submit Form */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await Promise.all(
        requirements.map((requirement) =>
          addDocument("requirements", {
            ...requirement,
            clientId: selectedClient?.id,
          })
        )
      );

      toast({
        title: "Requirements Added Successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error("Error adding requirements:", error);
      toast({
        title: "Error adding requirements",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW="90%">
        <ModalHeader bg="gray.700" color="white">
          Add Requirements
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={0} maxH="60vh">
          {/* Table for Bulk Add Requirements */}
          <TableContainer overflowY="auto" h="60vh">
            <Table variant="simple" size="sm" bg="gray.50">
              <Thead
                position="sticky"
                top="0"
                zIndex="100"
                bg="gray.100"
                boxShadow="md"
              >
                <Tr>
                  <Th>Requirement Title</Th>
                  <Th maxW={"150px"}>Related Deliverable</Th>
                  <Th minW={"350px"}>Requirement Detail</Th>
                  <Th maxW={"100px"}>Assigned To</Th>
                  <Th maxW={"100px"}>Due Date</Th>
                  {/* <Th>Raised By</Th> */}
                  <Th maxW={"50px"}></Th> {/* Remove button column */}
                </Tr>
              </Thead>
              <Tbody>
                {requirements.map((requirement, index) => (
                  <Tr key={index}>
                    {/* Requirement Name */}
                    <Td>
                      <Input
                        name="title"
                        value={requirement.title}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      />
                    </Td>

                    {/* Related Deliverable */}
                    <Td maxW={"150px"}>
                      <Select
                        name="deliverableId"
                        value={requirement.deliverableId}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      >
                        <option value="">Select</option>
                        {deliverables.map((del) => (
                          <option key={del.id} value={del.id}>
                            {del.name}
                          </option>
                        ))}
                      </Select>
                    </Td>

                    {/* Requirement Detail */}
                    <Td minW={"350px"}>
                      <Textarea
                        name="detail"
                        value={requirement.detail}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      />
                    </Td>

                    {/* Assigned To */}
                    <Td maxW={"100px"}>
                      <Select
                        name="assignedTo"
                        value={requirement.assignedTo}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      >
                        <option value="">Select</option>
                        <option value="Ryan Bentham">Ryan Bentham</option>
                      </Select>
                    </Td>

                    {/* Due Date */}
                    <Td maxW={"100px"}>
                      <Input
                        type="date"
                        name="dueDate"
                        value={requirement.dueDate}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      />
                    </Td>

                    {/* Raised By */}
                    {/* <Td>
                      <Input
                        name="raisedBy"
                        value={requirement.raisedBy}
                        onChange={(e) => handleInputChange(index, e)}
                        size="sm"
                        bg="white"
                      />
                    </Td> */}

                    {/* Remove Row Button */}
                    <Td textAlign="center" p={2} maxW={"50px"}>
                      <IconButton
                        size="xs"
                        colorScheme="red"
                        onClick={() => removeRequirement(index)}
                        isDisabled={requirements.length === 1}
                        icon={<DeleteIcon />}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>
        <Divider />
        <ModalFooter w="full">
          {/* Add Row Button */}
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={addRequirement}
            fontSize={"xs"}
          >
            Add Another Row
          </Button>
          <Spacer />
          <HStack>
            <Button
              fontSize={"xs"}
              mr={3}
              colorScheme="green"
              onClick={handleSubmit}
              loadingText="Submitting"
              isLoading={isSubmitting}
              isDisabled={requirements.some(
                (r) =>
                  !r.title || !r.detail || !r.deliverableId || !r.assignedTo
              )}
            >
              Submit Requirements
            </Button>
            <Button
              fontSize={"xs"}
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
            >
              Cancel
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BulkAddRequirementsModal;
